import {
  EEventStatus,
  EventDocData,
  EventTypes,
  GifEventAssets,
  TEventDetail,
  TRecentEventData,
} from "@letshang/constants"
import { flatMap, sortBy } from "lodash-es"
import moment from "moment"
import { atom, selector } from "recoil"

import persistAtom from "@fuse/recoil/recoilPersist"

import { EBuyTicketSessionKeys } from "@fuse/pages/eventSignUp/BuyTickets/utils"

export type EBuyTicketSessionKeyType =
  (typeof EBuyTicketSessionKeys)[keyof typeof EBuyTicketSessionKeys]

export type TBuyTicketsSessionStateType = Record<
  EBuyTicketSessionKeyType,
  any[]
> // Define correct type

interface TEventDetailsState {
  eventDetails: TEventDetail | null
  eventType: EEventStatus | null
  isLoading: boolean
  hasError: boolean
  doesNotExist: boolean
}

export const eventDetailsState = atom<TEventDetailsState>({
  key: "eventDetailsState",
  default: {
    eventDetails: null,
    eventType: null,
    isLoading: false,
    hasError: false,
    doesNotExist: false,
  },
})

export const allEventCacheState = atom({
  key: "allEventCacheState",
  default: {},
  effects_UNSTABLE: [persistAtom],
})

export const eventAtom = atom<EventTypes>({
  key: "eventAtom",
  default: {
    userCreatedOpenEvents: [],
    userCreatedClosedEvents: [],
    userInvitedOpenEvents: [],
    userInvitedClosedEvents: [],
    coHostedEvents: [],
  },
  effects_UNSTABLE: [persistAtom],
})

export const eventSelectors = selector({
  key: "eventSelectors",
  get: ({ get }) => get(eventAtom),
})

export const allEventsSelectors = selector({
  key: "allEventsSelectors",
  get: ({ get }) => {
    const events = get(eventSelectors)

    // Combine all event arrays into one
    const combinedEvents = flatMap([
      events.userCreatedOpenEvents,
      events.userCreatedClosedEvents,
      events.userInvitedOpenEvents,
      events.userInvitedClosedEvents,
      events.coHostedEvents,
    ])

    // Sort by event start time using Moment.js
    return sortBy(combinedEvents, (event) =>
      moment(event?.timing?.eventStartTime).valueOf(),
    )
  },
})

export const landingGifCarouselData = atom<GifEventAssets[]>({
  key: "landingGifCarouselData",
  default: [
    {
      url: "https://firebasestorage.googleapis.com/v0/b/walkme-e7896.appspot.com/o/event%20assets%2Ffilm.png?alt=media&token=83e6ae8d-5ae9-4c14-999b-39466014f59f",
      search_terms: ["movie", "film", "hollywood"],
      action: "create-event",
      type: "suggestions",
      month: "default",
      message:
        "Catch premieres, hit food trucks, and explore with your crew. Let’s make it a day. 🍿",
    },
  ],
  effects_UNSTABLE: [persistAtom], // Enable persistence
})

export const recentViewEventData = atom<TRecentEventData[]>({
  key: "recentViewEventData",
  default: [],
  effects_UNSTABLE: [persistAtom],
})

export const userVisitedInvitesData = atom<EventDocData[]>({
  key: "userVisitedInvites",
  default: [],
  effects_UNSTABLE: [persistAtom],
})

export const buyTicketsSessionState = atom<TBuyTicketsSessionStateType>({
  key: "buyTicketsSessionState",
  default: {
    [EBuyTicketSessionKeys.EVENTS_TICKETS]: [],
    [EBuyTicketSessionKeys.TICKETS_FORM_DATA]: [],
    [EBuyTicketSessionKeys.GROUP_TICKETS_INFO_STATE]: [],
  },
  effects_UNSTABLE: [persistAtom],
})

interface TEventMetadata {
  eventName?: string
  eventDate?: string
  actualFlyerUrl?: string
  location?: {
    eventLocation: string
    otherLocationDetails: string
    latitude: string
    longitude: string
    radius: number
    guestLocationPreview: string
  }
  eventDescription?: string
}

export const eventMetadataState = atom<TEventMetadata>({
  key: "eventMetadataState",
  default: {},
  effects_UNSTABLE: [persistAtom],
})
